<template>
    <div id="variables">
        <div class="templates-header">
            <h2 class="d-none d-sm-block">
                {{
                    $t("entities.templates.edit-template", {
                        title: selectedTemplate.title,
                    })
                }}
            </h2>
            <p class="d-none d-sm-block">
                {{ $t("entities.templates.message-preview") }}
            </p>

            <b-overlay :show="isLoadingMessageExample">
                <b-card bg-variant="whatsapp-bg p-1" no-body>
                    <pre class="template-preview bg-whatsapp-bg m-0">{{ selectedTemplate.final }}</pre>
                </b-card>
            </b-overlay>
        </div>

        <validation-observer ref="simpleRules">
            <perfect-scrollbar class="scroll-area">
                <div v-for="variable in selectedTemplate.variables">
                    <validation-provider #default="{ errors }" :name="`custom field ${variable.name}`" rules="required">
                        <b-form-group>
                            <label>Custom field {{ variable.name }}</label>

                            <div class="input-group">
                                <b-form-input
                                    :ref="`var[]`"
                                    v-model="variable.value"
                                    :data-index="variable.index"
                                    :name="`var-${variable.index}`"
                                    :placeholder="variable.name"
                                    :state="errors.length > 0 ? false : null"
                                    autocomplete="off"
                                    @input="input(variable.name, variable.index, $event)"
                                />

                                <emoji-picker @select-emoji="selectEmoji($event, variable)" />
                            </div>

                            <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                    </validation-provider>
                </div>

                <div v-if="selectedTemplate.mediaType && selectedTemplate.assetId">
                    <h3>{{ $t("entities.templates.attachment.title") }}</h3>
                    <p>
                        <font-awesome-icon
                            v-if="selectedTemplate.assetIsLocked"
                            :icon="['fas', 'lock']"
                            class="mr-25"
                            size="1x"
                        />
                        <font-awesome-icon
                            v-else="selectedTemplate.assetIsLocked"
                            :icon="['fas', 'lock-open']"
                            class="mr-25"
                            size="1x"
                        />
                        {{
                            $t(
                                selectedTemplate.assetIsLocked
                                    ? "entities.templates.attachment.hover-info-locked"
                                    : "entities.templates.attachment.hover-info"
                            )
                        }}
                    </p>
                </div>

                <div class="templates-footer">
                    <b-row no-gutters>
                        <b-col cols="6">
                            <b-button type="submit" variant="light" @click="$emit('reset-template')">
                                {{ $t("Back") }}
                            </b-button>
                        </b-col>
                        <b-col class="text-right" cols="6">
                            <b-button type="submit" variant="primary" @click.prevent="validationForm">
                                <b-spinner v-if="isLoadingSubmit" small></b-spinner>
                                {{ $t("entities.templates.use-this-template") }}
                            </b-button>
                        </b-col>
                    </b-row>
                </div>
            </perfect-scrollbar>
        </validation-observer>
    </div>
</template>

<script>
import { getUserData } from "@/auth/getUserData";
import useActiveChatTemplates from "@/composables/useActiveChatTemplates";
import useParseMessage from "@/composables/v2.1/useParseMessage";
import { addDataToInput } from "@/utils/input";
import { required } from "@validations";
import { onMounted, ref } from "@vue/composition-api";
import {
    BButton,
    BCard,
    BCardBody,
    BCardText,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BOverlay,
    BRow,
    BSpinner,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import emojiPicker from "@/components/emojiPicker";

export default {
    components: {
        ValidationProvider,
        ValidationObserver,
        required,
        BCardText,
        BFormInput,
        BFormGroup,
        BForm,
        BRow,
        BCol,
        BButton,
        BCard,
        BCardBody,
        BOverlay,
        BSpinner,
        emojiPicker,
    },
    props: {
        selectedTemplate: {
            type: Object,
            required: true,
        },
        activeChat: {
            type: Object,
            required: true,
        },
    },
    methods: {
        validationForm() {
            this.isLoadingSubmit = true;

            this.$refs.simpleRules.validate().then(async (success) => {
                if (success) {
                    this.selectedTemplate.content = this.selectedTemplate.final;
                    this.$emit("set-template", this.selectedTemplate);

                    this.isLoadingSubmit = false;
                }
            });
        },
    },
    setup(props, { emit }) {
        const isLoadingMessageExample = ref(false);
        const isLoadingSubmit = ref(false);

        const { variablesToIndex } = useActiveChatTemplates(props.activeChat);

        const { parseMessage } = useParseMessage();

        props.selectedTemplate.indexed = variablesToIndex(props.selectedTemplate.originalContent);

        onMounted(async () => {
            isLoadingMessageExample.value = true;

            const parsedMessage = await parseMessage({
                inputMessage: props.selectedTemplate.content,
                parseData: {
                    contact: props.activeChat.contact.id,
                    assignedAgent: props.activeChat.lastConversation.assignedUserId,
                    sendingAgent: getUserData(),
                },
            });

            props.selectedTemplate.variables.forEach((variable) => {
                const parsedVariable =
                    parsedMessage.variables[variable.name.substring(2, variable.name.length - 2).trim()];

                if (!parsedVariable) {
                    return;
                }

                variable.value = parsedVariable;
            });

            props.selectedTemplate.content = props.selectedTemplate.indexed;
            props.selectedTemplate.content = props.selectedTemplate.content.replace(/{{\s*([^}]*)\s*}}/g, (_, $1) => {
                return props.selectedTemplate.variables[$1 - 1].value;
            });

            emit("update:selected-template", {
                ...props.selectedTemplate,
                final: props.selectedTemplate.content,
            });

            isLoadingMessageExample.value = false;
        });

        const input = (name, index, event) => {
            props.selectedTemplate.variables[index - 1] = {
                index,
                name,
                value: event,
            };

            props.selectedTemplate.content = props.selectedTemplate.indexed;

            props.selectedTemplate.content = props.selectedTemplate.content.replace(/{{\s*([^}]*)\s*}}/g, (_, $1) => {
                return props.selectedTemplate.variables[$1 - 1].value;
            });

            emit("update:selected-template", {
                ...props.selectedTemplate,
                final: props.selectedTemplate.content,
            });
        };

        const selectEmoji = (emoji, variable) => {
            const newValue = addDataToInput(
                `var-${variable.index}`,
                props.selectedTemplate.variables[variable.index - 1].value || "",
                emoji.data
            );

            props.selectedTemplate.variables[variable.index - 1].value = newValue;
            input(variable.name, variable.index, newValue);
        };

        return {
            input,
            selectEmoji,
            isLoadingMessageExample,
            isLoadingSubmit,
        };
    },
};
</script>
